import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import Select from "react-select";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { documentTitle } from "../../../helpers/documentTitle";
import axios from "axios";
import { baseURL } from "../../../helpers/urlConfig/endPoints";
import { getCurrentDate } from "../../../helpers/dateParser";
import { generateOverallCustomerOB, generateSummary } from "./ReceiptGenerationHelper";
// import { currentDate, generateSummary } from "../support/receiptGeneration";
// import { selectTheCustomerAlert } from "../support/sweetAlert";
// import { generateOverallCustomerOB } from "./generateOverAllOB";

const CustomerList = () => {
  documentTitle("Customers");

  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerOrderedData, setCustomerOrderedData] = useState([]);
  const [newCustomer, setNewCustomer] = useState({ customer_id: "" });
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [purityFilter, setPurityFilter] = useState("");
  const [ob, setOb] = useState(0); // Assuming OB is a prop or state

  const fetchCustomerData = async () => {
    try {
      const response = await axios.get(`${baseURL}/customers/getCustomer`);
      if (response.mData) {
        const activeCustomers = response.mData.filter(
          (data) => data.is_delete !== 1
        );
        const skvCustomers = activeCustomers.filter(
          (data) => data.purity === "SKV/5" || data.purity === "SKV?80"
        );
        setCustomerOptions(skvCustomers);
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    fetchCustomerData();
  }, []);

  const handleInputChange = async (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setNewCustomer({ customer_id: value });

    try {
      const response = await axios.post(
        `${baseURL}/customers/getCustomerMappedOrders`,
        {
          customer_id: value,
        }
      );
      if (response.mStatus === 200) {
        const orderedDataWithIds = response.mData.map((row, index) => ({
          ...row,
          id: index + 1,
        }));
        const nonDeletedData = orderedDataWithIds.filter(
          (items) => items.is_delete == 0
        );
        setCustomerOrderedData(nonDeletedData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleOrderStatusFilterChange = (e) => {
    setOrderStatusFilter(e.target.value);
  };

  const handlePurityFilterChange = (e) => {
    setPurityFilter(e.target.value);
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
  };

  const filteredCustomerOrderedData = customerOrderedData.filter((item) => {
    if (orderStatusFilter !== "all") {
      if (item.order_status.toString() !== orderStatusFilter) {
        return false;
      }
    }

    if (purityFilter && item.purity !== purityFilter) {
      return false;
    }

    if (fromDate && toDate) {
      const OD = item.order_date === null ? "0000-00-00" : item.order_date;
      const orderDate = new Date(OD.split("T")[0]);
      const fromDateObj = new Date(fromDate);
      const toDateObj = new Date(toDate);

      if (orderDate < fromDateObj || orderDate > toDateObj) {
        return false;
      }
    }

    return true;
  });

  const selectedCustomerName =
    customerOptions.find(
      (customer) => customer.customer_id === newCustomer.customer_id
    )?.contact_person_name || "";

  const getOBCustomerDetails = async (customer_id) => {
    try {
      return await axios.post(`${baseURL}/ob/getOB`, {
        customer_id: parseInt(customer_id),
      });
    } catch (error) {
      console.error(error.message);
    }
  };

  const processedDate = (date) => {
    if (date == null) {
      return "0000-00-00";
    }
    const formattedDate = date.split("T")[0];
    return formattedDate;
  };

  const generateReceipt = async () => {
    if (!newCustomer.customer_id) {
      alert("Please Select The Customer");
      return;
    }

    let customerOldBalance = await getOBCustomerDetails(
      newCustomer.customer_id
    );

    let maaralJSON = customerOldBalance.mData[0]?.maaral_json
      ? JSON.parse(customerOldBalance.mData[0].maaral_json)
      : [];

    let totalCustomerOBAmount = 0;
    let totalCustomerMaaralAmount = 0;

    customerOldBalance.mData.forEach((item) => {
      totalCustomerOBAmount += parseFloat(item.ob_amount) || 0;
    });

    maaralJSON.forEach((item) => {
      let amount = parseFloat(item.maaral_amount);
      if (!isNaN(amount)) {
        totalCustomerMaaralAmount += amount;
      }
    });

    const reviewedData = filteredCustomerOrderedData.filter(
      (item) => item.is_reviewed == 1
    );

    const filteredData = reviewedData.filter((item) => item.is_delete === 0);

    filteredData.sort((a, b) => {
      const dateA = new Date(processedDate(a.order_date));
      const dateB = new Date(processedDate(b.order_date));
      return dateA - dateB;
    });

    const receiptContent = filteredData.map((item) => {
      const orderDate = new Date(item.order_date);
      const day = orderDate.getDate();
      const month = orderDate.getMonth() + 1;
      const formattedDay = day < 10 ? "0" + day : day;
      const formattedMonth = month < 10 ? "0" + month : month;
      const formattedDate = formattedDay + "-" + formattedMonth;

      return {
        order_id: item.order_id,
        product: item.description,
        weight: item.weight,
        orderDate: formattedDate,
      };
    });

    const totalWeight = receiptContent
      .reduce((total, item) => total + parseFloat(item.weight), 0)
      .toFixed(3);

    let adjustedTotalWeight = parseFloat(totalWeight);
    const productSummary = {};
    receiptContent.forEach((item) => {
      const productName = item.product.trim();
      if (
        productName === "70" ||
        productName === "80" ||
        productName === "WS"
      ) {
        if (!productSummary[productName]) {
          productSummary[productName] = 0;
        }
        const weight = parseFloat(item.weight);
        productSummary[productName] += weight;
        adjustedTotalWeight -= weight;
      }
    });

    const adjustedTotalWeightStr = adjustedTotalWeight.toFixed(3);

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
  <html>
    <head>
      <title>Print Receipt</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          font-size: 15px;
          margin: 20px;
          padding: 0;
          box-sizing: border-box;
        }
        table {
          border-collapse: collapse;
          width: 70%; /* Set table width to 70% to leave space on the right */
          margin-left: 0; /* Align table to the left */
          margin-right: auto; /* Leave space on the right */
          font-size: 15px;
        }
        th, td {
          border: 1px solid black;
          padding: 8px;
          text-align: left;
          vertical-align: top;
        }
        th {
          background-color: #f2f2f2;
        }
        /* Specific column widths */
        td:nth-child(1), th:nth-child(1) {
          width: 20%; /* Date column */
        }
        td:nth-child(2), th:nth-child(2) {
          width: 15%; /* Weight column */
        }
        td:nth-child(3), th:nth-child(3) {
          width: 65%; /* Details column */
        }
        button {
          margin-top: 20px;
          padding: 10px 20px;
          background-color: #4caf50;
          color: white;
          border: none;
          cursor: pointer;
          display: block;
          margin-left: 0; /* Align button with table */
        }
        button:hover {
          background-color: #45a049;
        }
        h3, h4, p, h1 {
          text-align: center;
          font-size: 15px;
          margin: 10px 0;
        }
        .spacing {
          margin: 5px 0;
        }
        .header-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          flex-wrap: wrap;
          width: 70%; /* Match table width */
          margin-left: 0; /* Align with table */
        }
        /* Responsive design */
        @media print {
          body {
            margin: 0;
            font-size: 12px;
          }
          table {
            width: 80%; /* Slightly wider for print to utilize space */
            margin-left: 0;
            margin-right: auto;
          }
          .header-info {
            width: 80%;
          }
          button {
            display: none; /* Hide button in print view */
          }
        }
        @media screen and (max-width: 600px) {
          table {
            width: 100%; /* Full width on small screens */
            margin-right: 0;
          }
          .header-info {
            width: 100%;
          }
          table, th, td {
            font-size: 12px;
          }
          td:nth-child(1), th:nth-child(1) {
            width: 25%;
          }
          td:nth-child(2), th:nth-child(2) {
            width: 25%;
          }
          td:nth-child(3), th:nth-child(3) {
            width: 50%;
          }
        }
      </style>
    </head>
    <body>
      <p style="text-align: center; font-size: 13px">உ</p>
      <h3 style="font-size: 15px">${selectedCustomerName}</h3>
      <div class="header-info">
        <h5 style="font-size: 10px">Date: ${getCurrentDate()}</h5>
      </div>
      <table>
        <tr>
          <th>Date</th>
          <th>Weight</th>
          <th>Details</th>
        </tr>
        <tbody>
          ${receiptContent
            .map(
              (item) => `
              <tr>
                <td>${item.orderDate}</td>
                <td>${item.weight}</td>
                <td>${item.product}</td>
              </tr>
            `
            )
            .join("")}
          <tr>
            <td colspan="2" style="text-align: right;">Total Weight:</td>
            <td>${adjustedTotalWeightStr}</td>
          </tr>
          <tr>
            <td colspan="2" style="text-align: right;">OB:</td>
            <td>${ob}</td>
          </tr>
          ${Object.keys(productSummary)
            .map(
              (productName) => `
              <tr>
                <td colspan="2" style="text-align: right;"><strong>${productName}</strong> Weight:</td>
                <td>${productSummary[productName].toFixed(3)}</td>
              </tr>
            `
            )
            .join("")}
        </tbody>
      </table>
      <br>
      ${
        parseInt(totalCustomerOBAmount) > 0
          ? `<h2>OB</h2>
            <table>
              <tr>
                <th>Date</th>
                <th>Details</th>
                <th>Amount</th>
              </tr>
              <tbody>
                ${customerOldBalance.mData
                  .map(
                    (item) => `
                    <tr>
                      <td>${item.created_at.split("T")[0] || "-"}</td>
                      <td>${item.ob || "-"}</td>
                      <td>${item.ob_amount || "-"}</td>
                    </tr>
                  `
                  )
                  .join("")}
                <tr>
                  <td>Total OB Amount</td>
                  <td></td>
                  <td>${totalCustomerOBAmount || "-"}</td>
                </tr>
              </tbody>
            </table>
            <br>`
          : ""
      }
      ${
        parseInt(totalCustomerMaaralAmount) > 0
          ? `<h2>Maaral</h2>
            <table>
              <tr>
                <th>Date</th>
                <th>Details</th>
                <th>Amount</th>
              </tr>
              <tbody>
                ${maaralJSON
                  .map(
                    (item) => `
                    <tr>
                      <td>${
                        customerOldBalance.mData[0]?.created_at.split("T")[0] ||
                        "-"
                      }</td>
                      <td>${item.maaral || "-"}</td>
                      <td>${item.maaral_amount || "-"}</td>
                    </tr>
                  `
                  )
                  .join("")}
                <tr>
                  <td>Total Maaral Amount</td>
                  <td></td>
                  <td>${totalCustomerMaaralAmount || "-"}</td>
                </tr>
              </tbody>
            </table>`
          : ""
      }
      <h3>Thank You</h3>
      <h3>SKV</h3>
    </body>
  </html>
`);
    printWindow.document.close();
    printWindow.print();

    ownerReceipt();

    if (printWindow) {
      printWindow.close();
    }
  };

  const ownerReceipt = async () => {
    const reviewedData = filteredCustomerOrderedData.filter(
      (item) => item.is_reviewed == 1
    );

    let customerOldBalance = await getOBCustomerDetails(
      newCustomer.customer_id
    );

    let maaralJSON = customerOldBalance.mData[0]?.maaral_json
      ? JSON.parse(customerOldBalance.mData[0].maaral_json)
      : [];

    let totalCustomerOBAmount = 0;
    let totalCustomerMaaralAmount = 0;
    customerOldBalance.mData.map(
      (item) => (totalCustomerOBAmount += parseFloat(item.ob_amount))
    );
    maaralJSON.map(
      (item) => (totalCustomerMaaralAmount += parseFloat(item.maaral_amount))
    );

    const filteredData = reviewedData.filter((item) => item.is_delete === 0);

    filteredData.sort((a, b) => {
      const dateA = new Date(processedDate(a.order_date));
      const dateB = new Date(processedDate(b.order_date));
      return dateA - dateB;
    });

    const receiptContent = filteredData.map((item) => {
      const OD = item.order_date == null ? "0000-00-00" : item.order_date;
      const orderDate = new Date(OD);
      const day = orderDate.getDate();
      const month = orderDate.getMonth() + 1;
      const formattedDay = day < 10 ? "0" + day : day;
      const formattedMonth = month < 10 ? "0" + month : month;
      const formattedDate = formattedDay + "-" + formattedMonth;

      return {
        order_id: item.order_id,
        product: item.description,
        weight: item.weight,
        orderDate: formattedDate,
      };
    });

    const totalWeight = receiptContent
      .reduce((total, item) => total + parseFloat(item.weight), 0)
      .toFixed(3);

    let adjustedTotalWeight = parseFloat(totalWeight);
    const productSummary = {};
    receiptContent.forEach((item) => {
      const productName = item.product.trim();
      if (
        productName === "70" ||
        productName === "80" ||
        productName === "WS"
      ) {
        if (!productSummary[productName]) {
          productSummary[productName] = 0;
        }
        const weight = parseFloat(item.weight);
        productSummary[productName] += weight;
        adjustedTotalWeight -= weight;
      }
    });

    const adjustedTotalWeightStr = adjustedTotalWeight.toFixed(3);

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
  <head>
    <title>Print Receipt</title>
    <style>
      body {
        font-family: Arial, sans-serif;
        font-size: 15px;
        margin: 20px;
        padding: 0;
        box-sizing: border-box;
      }
      table {
        border-collapse: collapse;
        width: 70%;
        margin-left: 2px;
        margin-right: auto;
        font-size: 15px;
      }
      th, td {
        border: 1px solid black;
        padding: 5px;
        text-align: left;
        vertical-align: top;
      }
      th {
        font-size: 15px;
        background-color: #f2f2f2;
      }
      /* Specific column widths for main table */
      td:nth-child(1), th:nth-child(1) {
        width: 10%; /* Order ID */
      }
      td:nth-child(2), th:nth-child(2) {
        width: 20%; /* Date */
      }
      td:nth-child(3), th:nth-child(3) {
        width: 55%; /* Details */
      }
      td:nth-child(4), th:nth-child(4) {
        width: 15%; /* Weight */
      }
      button {
        margin-top: 20px;
        padding: 10px 20px;
        background-color: #4caf50;
        color: white;
        border: none;
        cursor: pointer;
        display: block;
        margin-left: 2px;
      }
      button:hover {
        background-color: #45a049;
      }
      h3, h4, p, h1 {
        text-align: center;
        margin: 10px 0;
      }
      .spacing {
        margin: 5px 0;
      }
      .summary-table {
        width: 50%;
        margin-left: 2px;
        margin-right: auto;
        margin: 10px 0;
      }
      .summary-table th, .summary-table td {
        text-align: center;
        width: 33.33%; /* Equal width for summary table columns */
      }
      .header-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 70%;
        margin-left: 2px;
        margin-right: auto;
        margin-bottom: 10px;
        flex-wrap: wrap;
      }
      /* Responsive design */
      @media print {
        body {
          margin: 0;
          font-size: 12px;
        }
        table, .summary-table {
          width: 80%;
          margin-left: 0;
          margin-right: auto;
        }
        .header-info {
          width: 80%;
        }
        button {
          display: none;
        }
      }
      @media screen and (max-width: 600px) {
        table, .summary-table {
          width: 100%;
          margin-right: 0;
        }
        .header-info {
          width: 100%;
        }
        table, th, td {
          font-size: 12px;
        }
        td:nth-child(1), th:nth-child(1) {
          width: 15%;
        }
        td:nth-child(2), th:nth-child(2) {
          width: 25%;
        }
        td:nth-child(3), th:nth-child(3) {
          width: 45%;
        }
        td:nth-child(4), th:nth-child(4) {
          width: 15%;
        }
        .summary-table th, .summary-table td {
          width: 33.33%;
        }
      }
    </style>
  </head>
  <body>
    <p style="text-align: center">உ</p>
    <h3 style="margin-top: 3px">${selectedCustomerName}</h3>
    <div class="header-info">
      <h5>Date: ${getCurrentDate()}</h5>
    </div>
    <table>
      <tr>
        <th>Order ID</th>
        <th>Date</th>
        <th>Details</th>
        <th>Weight</th>
      </tr>
      <tbody>
        ${receiptContent
          .map(
            (item) => `
            <tr>
              <td>${item.order_id}</td>
              <td style="font-size: 15px">${item.orderDate}</td>
              <td>${item.product}</td>
              <td>${item.weight}</td>
            </tr>
          `
          )
          .join("")}
        <tr>
          <td colspan="3" style="text-align: right;">Total Weight:</td>
          <td>${adjustedTotalWeightStr}</td>
        </tr>
        <tr>
          <td colspan="3" style="text-align: right;">OB:</td>
          <td>${ob}</td>
        </tr>
        ${Object.keys(productSummary)
          .map(
            (productName) => `
            <tr>
              <td colspan="3" style="text-align: right;"><strong>${productName}</strong> Total Weight:</td>
              <td>${productSummary[productName].toFixed(3)}</td>
            </tr>
          `
          )
          .join("")}
      </tbody>
    </table>
    ${
      parseInt(totalCustomerOBAmount) > 0
        ? `<h2>OB</h2>
          <table class="summary-table">
            <tr>
              <th>Date</th>
              <th>Details</th>
              <th>Amount</th>
            </tr>
            <tbody>
              ${customerOldBalance.mData
                .map(
                  (item) => `
                  <tr>
                    <td>${item.created_at.split("T")[0] || "-"}</td>
                    <td>${item.ob || "-"}</td>
                    <td>${item.ob_amount || "-"}</td>
                  </tr>
                `
                )
                .join("")}
              <tr>
                <td>Total OB Amount</td>
                <td></td>
                <td>${totalCustomerOBAmount || "-"}</td>
              </tr>
            </tbody>
          </table>
          <br>`
        : ""
    }
    ${
      parseInt(totalCustomerMaaralAmount) > 0
        ? `<h2>Maaral</h2>
          <table class="summary-table">
            <tr>
              <th>Date</th>
              <th>Details</th>
              <th>Amount</th>
            </tr>
            <tbody>
              ${maaralJSON
                .map(
                  (item) => `
                  <tr>
                    <td>${
                      customerOldBalance.mData[0]?.created_at.split("T")[0] ||
                      "-"
                    }</td>
                    <td>${item.maaral || "-"}</td>
                    <td>${item.maaral_amount || "-"}</td>
                  </tr>
                `
                )
                .join("")}
              <tr>
                <td>Total Maaral Amount</td>
                <td></td>
                <td>${totalCustomerMaaralAmount || "-"}</td>
              </tr>
            </tbody>
          </table>`
        : ""
    }
    <h3>Thank You</h3>
    <h3>SKV</	</h3>
    <p>Own Copy</p>
  </body>
</html>
    `);

    printWindow.document.close();
    printWindow.print();
  };

  const generateSummaryReport = () => {
     generateSummary(filteredCustomerOrderedData);
  };

  const selectOptions = customerOptions.map((customer) => ({
    value: customer.customer_id,
    label: customer.contact_person_name,
  }));

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Customer Wise Receipt" pageTitle="Customer" />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Form>
                  <Row className="mb-3">
                    <Col md={3}>
                      <FormGroup>
                        <Label for="customerSelect">Select Customer</Label>
                        <Select
                          id="customerSelect"
                          options={selectOptions}
                          value={selectOptions.find(
                            (option) => option.value === newCustomer.customer_id
                          )}
                          onChange={handleInputChange}
                          placeholder="Select Customer"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label for="orderStatus">Order Status</Label>
                        <Input
                          type="select"
                          id="orderStatus"
                          value={orderStatusFilter}
                          onChange={handleOrderStatusFilterChange}
                        >
                          <option value="all">All</option>
                          <option value="0">Pending</option>
                          <option value="2">Closed</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label for="purity">Group</Label>
                        <Input
                          type="select"
                          id="purity"
                          value={purityFilter}
                          onChange={handlePurityFilterChange}
                        >
                          <option value="">All</option>
                          <option value="SKV/5">SKV/5</option>
                          <option value="SKV?80">SKV?80</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label for="fromDate">From Date</Label>
                        <Input
                          type="date"
                          id="fromDate"
                          value={fromDate}
                          onChange={handleFromDateChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label for="toDate">To Date</Label>
                        <Input
                          type="date"
                          id="toDate"
                          value={toDate}
                          onChange={handleToDateChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={2}>
                      <Button color="primary" block onClick={generateReceipt}>
                        Print Receipt
                      </Button>
                    </Col>
                    <Col md={2}>
                      <Button
                        color="primary"
                        block
                        onClick={generateSummaryReport}
                      >
                        Summary
                      </Button>
                    </Col>
                    <Col md={2}>
                      <Button
                        color="primary"
                        block
                        onClick={generateOverallCustomerOB}
                      >
                        Generate Over All OBs
                      </Button>
                    </Col>
                  </Row>
                </Form>
                <div id="customerList">
                  <div className="table-responsive table-card mt-3 mb-1">
                    <table className="table align-middle table-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>SL.No</th>
                          {/* <th>Order ID</th> */}
                          {/* <th>Group</th> */}
                          {/* <th>Description</th> */}
                          {/* <th>Order Status</th> */}
                          {/* <th>Is Reviewed</th> */}
                          <th>Order Date</th>
                          <th>Customer Name</th>
                          <th>Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCustomerOrderedData.map((customer, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{processedDate(customer.order_date) || "-"}</td>
                            {/* <td>{customer.order_id || "-"}</td> */}
                            <td>{customer.contact_person_name || "-"}</td>
                            {/* <td>{customer.purity || "-"}</td> */}
                            {/* <td>{customer.description || "-"}</td> */}
                            <td>{customer.weight || "-"}</td>
                            {/* <td>
                              {customer.order_status === 0
                                ? "Pending"
                                : customer.order_status === 1
                                ? "InProgress"
                                : customer.order_status === 2
                                ? "Completed"
                                : "-"}
                            </td> */}
                            {/* <td>
                              {customer.is_reviewed === 0
                                ? "Pending"
                                : "Approved"}
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredCustomerOrderedData.length === 0 && (
                      <div className="text-center mt-4">
                        <p>No customers found.</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CustomerList;
